import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Upload, FileText, Landmark, Check, X, AlertTriangle, TrendingUp, TrendingDown, Clock, Euro, FileSpreadsheet, Scale, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComptabilitePage() {
  const { apiFetch, API } = useAuth();
  const [summary, setSummary] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [reconciliations, setReconciliations] = useState([]);
  const [bankImports, setBankImports] = useState([]);
  const [unpaidFactures, setUnpaidFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ facture_id: "", amount: "", bank_date: "", bank_ref: "", bank_label: "" });
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    const [sumRes, trsRes, recRes, impRes, facRes] = await Promise.all([
      apiFetch(`${API}/comptabilite/summary?year=${year}`),
      apiFetch(`${API}/comptabilite/treasury`),
      apiFetch(`${API}/comptabilite/reconciliations`),
      apiFetch(`${API}/comptabilite/bank-imports`),
      apiFetch(`${API}/factures`)
    ]);
    if (sumRes.ok) setSummary(await sumRes.json());
    if (trsRes.ok) setTreasury(await trsRes.json());
    if (recRes.ok) setReconciliations(await recRes.json());
    if (impRes.ok) setBankImports(await impRes.json());
    if (facRes.ok) {
      const factures = await facRes.json();
      setUnpaidFactures(factures.filter(f => f.status === "emise"));
    }
    setLoading(false);
  }, [apiFetch, API, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadFEC = () => {
    window.open(`${BACKEND_URL}/api/comptabilite/export-fec?year=${year}`, "_blank");
  };

  const downloadCSV = (docType = "all") => {
    window.open(`${BACKEND_URL}/api/comptabilite/export-csv?year=${year}&doc_type=${docType}`, "_blank");
  };

  const handleBankImport = async (files) => {
    if (!files?.length) return;
    const formData = new FormData();
    formData.append("file", files[0]);
    try {
      const res = await fetch(`${API}/comptabilite/bank-import`, {
        method: "POST", body: formData, credentials: "include"
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchData();
      } else {
        toast.error(data.detail || "Erreur d'import");
      }
    } catch { toast.error("Erreur d'import"); }
  };

  const handleValidateRec = async (recId) => {
    const res = await apiFetch(`${API}/comptabilite/reconciliations/${recId}/validate`, { method: "POST" });
    if (res.ok) { toast.success("Rapprochement validé"); fetchData(); }
  };

  const handleRejectRec = async (recId) => {
    const res = await apiFetch(`${API}/comptabilite/reconciliations/${recId}/reject`, { method: "POST" });
    if (res.ok) { toast.success("Rapprochement rejeté"); fetchData(); }
  };

  const handleManualRec = async () => {
    if (!manualForm.facture_id || !manualForm.amount) { toast.error("Facture et montant requis"); return; }
    const res = await apiFetch(`${API}/comptabilite/reconciliations/manual`, {
      method: "POST",
      body: JSON.stringify({ ...manualForm, amount: parseFloat(manualForm.amount) })
    });
    if (res.ok) { toast.success("Rapprochement validé"); setManualDialogOpen(false); setManualForm({ facture_id: "", amount: "", bank_date: "", bank_ref: "", bank_label: "" }); fetchData(); }
  };

  const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  if (loading) return <div className="p-4 sm:p-8"><div className="animate-pulse h-96 bg-slate-200 rounded-lg" /></div>;

  return (
    <div className="p-4 sm:p-8" data-testid="comptabilite-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">Comptabilité</h1>
          <p className="text-sm text-slate-500 mt-1">Exports fiscaux, trésorerie et rapprochements bancaires</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="exports" data-testid="tab-exports">Exports Comptables</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank">Rapprochement Bancaire</TabsTrigger>
          <TabsTrigger value="treasury" data-testid="tab-treasury">Trésorerie</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview">
          {summary && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">CA Facturé</p>
                        <p className="text-2xl font-bold font-barlow text-slate-900 mt-1">{fmt(summary.ca_emis + summary.ca_encaisse)} EUR</p>
                      </div>
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Euro className="w-5 h-5 text-blue-600" /></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">CA Encaissé</p>
                        <p className="text-2xl font-bold font-barlow text-emerald-600 mt-1">{fmt(summary.ca_encaisse)} EUR</p>
                      </div>
                      <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Bons de Commande</p>
                        <p className="text-2xl font-bold font-barlow text-indigo-600 mt-1">{fmt(summary.total_bc)} EUR</p>
                        <p className="text-[10px] text-slate-400">{summary.nb_bc || 0} BC dont {summary.nb_bc_non_facture || 0} sans facture ({fmt(summary.bc_non_facture)} EUR)</p>
                      </div>
                      <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-indigo-600" /></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Acomptes</p>
                        <p className="text-2xl font-bold font-barlow text-cyan-600 mt-1">{fmt(summary.total_acomptes)} EUR</p>
                        <p className="text-[10px] text-slate-400">{summary.nb_acomptes || 0} acompte(s)</p>
                      </div>
                      <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-cyan-600" /></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Impayés</p>
                        <p className="text-2xl font-bold font-barlow text-amber-600 mt-1">{fmt(summary.ca_emis)} EUR</p>
                      </div>
                      <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">TVA Collectée</p>
                        <p className="text-2xl font-bold font-barlow text-slate-700 mt-1">{fmt(summary.tva_collectee)} EUR</p>
                      </div>
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center"><Scale className="w-5 h-5 text-slate-600" /></div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly table */}
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Détail mensuel {year}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-semibold">Mois</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Nb Factures</TableHead>
                        <TableHead className="text-xs font-semibold text-right">CA HT</TableHead>
                        <TableHead className="text-xs font-semibold text-right">TVA</TableHead>
                        <TableHead className="text-xs font-semibold text-right">CA TTC</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Encaissé</TableHead>
                        <TableHead className="text-xs font-semibold text-right">BC</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Acomptes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.monthly?.map((m, idx) => {
                        const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
                        const acompte = summary.monthly_acomptes?.[idx] || {};
                        const bc = summary.monthly_bc?.[idx] || {};
                        return (
                          <TableRow key={m.month}>
                            <TableCell className="text-sm font-medium">{months[m.month - 1]} {year}</TableCell>
                            <TableCell className="text-sm text-right">{m.count}</TableCell>
                            <TableCell className="text-sm text-right">{fmt(m.ht)}</TableCell>
                            <TableCell className="text-sm text-right text-slate-400">{fmt(m.tva)}</TableCell>
                            <TableCell className="text-sm text-right font-medium">{fmt(m.ttc)}</TableCell>
                            <TableCell className="text-sm text-right text-emerald-600 font-medium">{fmt(m.encaisse)}</TableCell>
                            <TableCell className="text-sm text-right text-indigo-600 font-medium">{bc.count ? `${fmt(bc.total)} (${bc.count})` : "-"}</TableCell>
                            <TableCell className="text-sm text-right text-cyan-600 font-medium">{fmt(acompte.total)}</TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-slate-50 font-bold">
                        <TableCell className="text-sm">TOTAL {year}</TableCell>
                        <TableCell className="text-sm text-right">{summary.total_factures}</TableCell>
                        <TableCell className="text-sm text-right">{fmt(summary.monthly?.reduce((a, m) => a + m.ht, 0))}</TableCell>
                        <TableCell className="text-sm text-right">{fmt(summary.tva_collectee)}</TableCell>
                        <TableCell className="text-sm text-right">{fmt(summary.ca_emis + summary.ca_encaisse)}</TableCell>
                        <TableCell className="text-sm text-right text-emerald-600">{fmt(summary.ca_encaisse)}</TableCell>
                        <TableCell className="text-sm text-right text-indigo-600">{fmt(summary.total_bc)} ({summary.nb_bc})</TableCell>
                        <TableCell className="text-sm text-right text-cyan-600">{fmt(summary.total_acomptes)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* EXPORTS TAB */}
        <TabsContent value="exports">
          <div className="grid grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center"><FileText className="w-6 h-6 text-red-600" /></div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Export FEC</h3>
                    <p className="text-xs text-slate-500">Fichier des Ecritures Comptables (norme DGFiP)</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-4">Format obligatoire pour l'administration fiscale française. Contient toutes les écritures comptables (journal des ventes, banque) au format TSV normalisé.</p>
                <ul className="text-xs text-slate-500 space-y-1 mb-4">
                  <li>- Journaux: Ventes (VE), Banque (BQ)</li>
                  <li>- Comptes: 411xxx (clients), 706000 (prestations), 445710 (TVA), 512000 (banque)</li>
                  <li>- Lettrage automatique des factures payées</li>
                </ul>
                <Button onClick={downloadFEC} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="export-fec-btn">
                  <Download className="w-4 h-4 mr-2" /> Télécharger FEC {year}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center"><FileSpreadsheet className="w-6 h-6 text-emerald-600" /></div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Export CSV Détaillé</h3>
                    <p className="text-xs text-slate-500">Compatible Excel / logiciels comptables</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-4">Export détaillé ligne par ligne de tous les documents comptables avec montants HT, TVA, TTC, statuts et références croisées.</p>
                <div className="space-y-2">
                  <Button onClick={() => downloadCSV("all")} variant="outline" className="w-full justify-start" data-testid="export-csv-all-btn">
                    <Download className="w-4 h-4 mr-2" /> Tout exporter (Devis + BC + Factures)
                  </Button>
                  <Button onClick={() => downloadCSV("factures")} variant="outline" className="w-full justify-start text-emerald-700">
                    <Download className="w-4 h-4 mr-2" /> Factures uniquement
                  </Button>
                  <Button onClick={() => downloadCSV("devis")} variant="outline" className="w-full justify-start text-blue-700">
                    <Download className="w-4 h-4 mr-2" /> Devis uniquement
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* BANK RECONCILIATION TAB */}
        <TabsContent value="bank">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Rapprochement Bancaire</h2>
              <div className="flex items-center gap-3">
                <input ref={fileInputRef} type="file" accept=".csv,.pdf" className="hidden" onChange={e => handleBankImport(e.target.files)} />
                <Button onClick={() => fileInputRef.current?.click()} className="bg-[#1a3a5c] hover:bg-[#15304d] text-white" data-testid="import-bank-btn">
                  <Upload className="w-4 h-4 mr-2" /> Importer relevé bancaire
                </Button>
                <Button variant="outline" onClick={() => setManualDialogOpen(true)} data-testid="manual-rec-btn">
                  <Landmark className="w-4 h-4 mr-2" /> Rapprochement manuel
                </Button>
              </div>
            </div>

            {/* Pending reconciliations */}
            {reconciliations.filter(r => r.status === "pending").length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Rapprochements en attente de validation ({reconciliations.filter(r => r.status === "pending").length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs font-semibold">Facture</TableHead>
                        <TableHead className="text-xs font-semibold">Client</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Montant Facture</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Montant Banque</TableHead>
                        <TableHead className="text-xs font-semibold">Libellé Banque</TableHead>
                        <TableHead className="text-xs font-semibold">Confiance</TableHead>
                        <TableHead className="text-xs font-semibold w-28">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reconciliations.filter(r => r.status === "pending").map(r => (
                        <TableRow key={r.rec_id} data-testid={`rec-${r.rec_id}`}>
                          <TableCell className="text-sm font-medium">{r.facture_number}</TableCell>
                          <TableCell className="text-sm text-slate-500">{r.client_name}</TableCell>
                          <TableCell className="text-sm text-right">{fmt(r.facture_amount)} EUR</TableCell>
                          <TableCell className="text-sm text-right font-medium">{fmt(r.bank_amount)} EUR</TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-48 truncate">{r.bank_label}</TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] border-0 ${r.confidence >= 90 ? "bg-emerald-100 text-emerald-700" : r.confidence >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                              {r.confidence}%
                            </Badge>
                          </TableCell>
                          <TableCell className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleValidateRec(r.rec_id)} className="h-7 text-emerald-600 hover:text-emerald-700" data-testid={`validate-rec-${r.rec_id}`}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleRejectRec(r.rec_id)} className="h-7 text-red-500 hover:text-red-600" data-testid={`reject-rec-${r.rec_id}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Validated reconciliations */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Historique des rapprochements ({reconciliations.filter(r => r.status === "validated").length} validés)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {reconciliations.filter(r => r.status === "validated").length === 0 ? (
                  <div className="p-12 text-center">
                    <Landmark className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Aucun rapprochement validé</p>
                    <p className="text-slate-300 text-xs mt-1">Importez un relevé bancaire pour commencer</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs font-semibold">Facture</TableHead>
                        <TableHead className="text-xs font-semibold">Client</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Montant</TableHead>
                        <TableHead className="text-xs font-semibold">Date Banque</TableHead>
                        <TableHead className="text-xs font-semibold">Type</TableHead>
                        <TableHead className="text-xs font-semibold">Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reconciliations.filter(r => r.status === "validated").map(r => (
                        <TableRow key={r.rec_id}>
                          <TableCell className="text-sm font-medium">{r.facture_number}</TableCell>
                          <TableCell className="text-sm text-slate-500">{r.client_name}</TableCell>
                          <TableCell className="text-sm text-right font-medium">{fmt(r.bank_amount)} EUR</TableCell>
                          <TableCell className="text-sm text-slate-500">{r.bank_date || formatDate(r.validated_at)}</TableCell>
                          <TableCell><Badge className={`text-[10px] border-0 ${r.match_type === "auto" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600"}`}>{r.match_type === "auto" ? "Auto" : "Manuel"}</Badge></TableCell>
                          <TableCell><Badge className="bg-emerald-50 text-emerald-600 text-[10px] border-0">Validé</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Import history */}
            {bankImports.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-3"><CardTitle className="text-sm">Historique des imports</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs font-semibold">Fichier</TableHead>
                        <TableHead className="text-xs font-semibold">Date</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Transactions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bankImports.map(i => (
                        <TableRow key={i.import_id}>
                          <TableCell className="text-sm font-medium">{i.filename}</TableCell>
                          <TableCell className="text-sm text-slate-500">{formatDate(i.created_at)}</TableCell>
                          <TableCell className="text-sm text-right">{i.transaction_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* TREASURY TAB */}
        <TabsContent value="treasury">
          {treasury && (
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Impayés</p>
                    <p className="text-2xl font-bold font-barlow text-red-600 mt-1">{fmt(treasury.unpaid_total)} EUR</p>
                    <p className="text-xs text-slate-400 mt-0.5">{treasury.unpaid_count} facture(s)</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Encaissé ce mois</p>
                    <p className="text-2xl font-bold font-barlow text-emerald-600 mt-1">{fmt(treasury.paid_this_month)} EUR</p>
                    <p className="text-xs text-slate-400 mt-0.5">{treasury.paid_this_month_count} paiement(s)</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Encaissé {year}</p>
                    <p className="text-2xl font-bold font-barlow text-emerald-600 mt-1">{fmt(treasury.paid_this_year)} EUR</p>
                    <p className="text-xs text-slate-400 mt-0.5">{treasury.paid_this_year_count} paiement(s)</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Balance</p>
                    <p className={`text-2xl font-bold font-barlow mt-1 ${(treasury.paid_this_year - treasury.unpaid_total) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {fmt(treasury.paid_this_year - treasury.unpaid_total)} EUR
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">Encaissé - Impayés</p>
                  </CardContent>
                </Card>
              </div>

              {/* Aging Analysis */}
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-500" /> Ancienneté des créances</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { label: "0 - 30 jours", value: treasury.aging["0_30"], color: "bg-emerald-500" },
                      { label: "31 - 60 jours", value: treasury.aging["31_60"], color: "bg-amber-500" },
                      { label: "61 - 90 jours", value: treasury.aging["61_90"], color: "bg-orange-500" },
                      { label: "> 90 jours", value: treasury.aging["90_plus"], color: "bg-red-500" },
                    ].map(a => (
                      <div key={a.label} className="text-center">
                        <div className={`h-2 ${a.color} rounded-full mb-2`} style={{ width: `${Math.min(100, (a.value / Math.max(treasury.unpaid_total, 1)) * 100)}%`, minWidth: a.value > 0 ? "8px" : "0" }} />
                        <p className="text-lg font-bold font-barlow text-slate-900">{fmt(a.value)} EUR</p>
                        <p className="text-xs text-slate-400">{a.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Unpaid invoices list */}
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Factures en attente de paiement ({treasury.unpaid_count})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {treasury.unpaid_invoices?.length === 0 ? (
                    <div className="p-8 text-center">
                      <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">Toutes les factures sont payées</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-semibold">Facture</TableHead>
                          <TableHead className="text-xs font-semibold">Client</TableHead>
                          <TableHead className="text-xs font-semibold">Date</TableHead>
                          <TableHead className="text-xs font-semibold text-right">Montant TTC</TableHead>
                          <TableHead className="text-xs font-semibold">Ancienneté</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {treasury.unpaid_invoices?.map(f => {
                          const days = Math.floor((Date.now() - new Date(f.created_at).getTime()) / 86400000);
                          return (
                            <TableRow key={f.facture_id}>
                              <TableCell className="text-sm font-medium">{f.facture_number}</TableCell>
                              <TableCell className="text-sm text-slate-500">{f.client_name}</TableCell>
                              <TableCell className="text-sm text-slate-500">{formatDate(f.created_at)}</TableCell>
                              <TableCell className="text-sm text-right font-medium">{fmt(f.total)} EUR</TableCell>
                              <TableCell>
                                <Badge className={`text-[10px] border-0 ${days <= 30 ? "bg-emerald-50 text-emerald-600" : days <= 60 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>
                                  {days} jour{days > 1 ? "s" : ""}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Manual Reconciliation Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rapprochement manuel</DialogTitle>
            <DialogDescription>Lier manuellement un paiement à une facture</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Facture *</Label>
              <Select value={manualForm.facture_id} onValueChange={v => setManualForm(s => ({ ...s, facture_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Sélectionner une facture" /></SelectTrigger>
                <SelectContent>
                  {unpaidFactures.map(f => (
                    <SelectItem key={f.facture_id} value={f.facture_id}>{f.facture_number} - {f.client_name} ({fmt(f.total)} EUR)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Montant reçu *</Label>
                <Input type="number" step="0.01" value={manualForm.amount} onChange={e => setManualForm(s => ({ ...s, amount: e.target.value }))} placeholder="0.00" data-testid="manual-amount-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Date du paiement</Label>
                <Input type="date" value={manualForm.bank_date} onChange={e => setManualForm(s => ({ ...s, bank_date: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Référence bancaire</Label>
              <Input value={manualForm.bank_ref} onChange={e => setManualForm(s => ({ ...s, bank_ref: e.target.value }))} placeholder="Ex: VIR-2026-001" />
            </div>
            <div className="space-y-1.5">
              <Label>Libellé</Label>
              <Input value={manualForm.bank_label} onChange={e => setManualForm(s => ({ ...s, bank_label: e.target.value }))} placeholder="Libellé du virement" />
            </div>
            <Button onClick={handleManualRec} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="confirm-manual-rec-btn">
              <Check className="w-4 h-4 mr-2" /> Valider le rapprochement
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
